import { Link } from "react-router-dom";
import React, {
  useContext,
  useCallback,
  lazy,
  Suspense,
  useState,
  useEffect,
} from "react";
import { Primary } from "../buttons/Primary";
import { CreditUserContext } from "../../context/CreditUser/CreditUserContext";
import Particles from "react-tsparticles";
import { loadSlim } from "tsparticles-slim";

const Simulator = lazy(() =>
  import("../credits/Simulator").then((module) => ({
    default: module.Simulator,
  }))
);

export function Hero() {
  const { creditUser } = useContext(CreditUserContext);
  const [showParticles, setShowParticles] = useState(false);
  const [showSimulator, setShowSimulator] = useState(false);

  useEffect(() => {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => setShowParticles(true));
    } else {
      setTimeout(() => setShowParticles(true), 1000);
    }
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setShowSimulator(true);
    }, 1500);
    return () => clearTimeout(timeout);
  }, []);

  const particlesInit = useCallback(async (main) => {
    await loadSlim(main);
  }, []);

  return (
    <div className="bg-darkBg relative overflow-hidden font-walsheim">
      {/* Partículas renderizadas al fondo sin bloquear el render */}
      {showParticles && (
        <Particles
          id="hero-particles"
          init={particlesInit}
          options={{
            background: { color: "#1C1535" },
            fullScreen: { enable: false },
            particles: {
              number: { value: 40, density: { enable: true, value_area: 800 } },
              color: { value: "#ffffff" },
              shape: { type: "circle" },
              opacity: { value: 0.5 },
              size: { value: 2, random: true },
              move: { enable: true, speed: 0.2, direction: "none" },
            },
            interactivity: {
              detect_on: "canvas",
              events: {
                onHover: { enable: false },
                onClick: { enable: false },
              },
            },
          }}
          className="absolute inset-0 w-full h-full z-0"
        />
      )}

      <section className="pb-10 sm:pb-12 relative z-10" aria-labelledby="hero-heading">
        <main className="mt-24 sm:mt-28">
          <div className="mx-auto max-w-7xl">
            <div className="flex flex-col lg:grid lg:grid-cols-12 lg:gap-8">
              {/* Columna izquierda: texto principal */}
              <div className="z-10 px-4 text-white sm:px-6 sm:text-center md:max-w-2xl md:mx-auto lg:col-span-6 lg:text-left lg:flex lg:items-center">
                <div className="z-10">
                  <h1
                    id="hero-heading"
                    className="text-center md:text-left mt-4 text-4xl font-bold sm:mt-5 sm:leading-none lg:mt-6 lg:text-5xl xl:text-6xl"
                  >
                    <span className="md:block">
                      Explora un mundo de inclusión financiera
                    </span>
                  </h1>
                  <div className="text-center md:text-left lg:pr-10 py-8 text-xl">
                    <h2 id="hero-title">
                      En Galilea, la innovación y la seguridad se unen para
                      brindarte acceso a crédito sin barreras. Solicita en
                      Colombia desde $200.000 hasta $1.500.000 de forma 100%
                      digital, sin adelantos ni cobros ocultos, y recibe tu
                      dinero en 1 día hábil o menos.
                    </h2>
                  </div>
                </div>
              </div>

              {/* Columna derecha: simulador o placeholder */}
              <div className="z-10 relative mt-0 lg:mt-0 lg:col-span-6">
                <div className="relative pb-8">
                  <div className="bg-white sm:max-w-sm sm:mx-auto rounded-3xl sm:overflow-hidden px-4 py-8 sm:px-8 min-h-[420px] flex flex-col justify-center">
                    <div>
                      <h3 className="font-bold text-xl text-center">
                        Calcula tu crédito
                      </h3>
                      <div className="text-center mb-4">
                        <span className="text-purple">
                          Tu primera vez hasta $500.000
                        </span>
                      </div>
                    </div>

                    {showSimulator ? (
                      <Suspense
                        fallback={
                          <div className="h-[350px] animate-pulse flex items-center justify-center bg-gray-50">
                            Cargando calculadora...
                          </div>
                        }
                      >
                        <Simulator
                          title={"Elige el monto"}
                          title2={"Cuántas cuotas"}
                          title3={"Elige la fecha de pago"}
                          maxAmn={1500000}
                          minAmn={200000}
                          step={10000}
                          default={350000}
                        />
                      </Suspense>
                    ) : (
                      <div className="h-[350px] animate-pulse flex items-center justify-center bg-gray-100 text-gray-500 rounded-lg">
                        Cargando calculadora...
                      </div>
                    )}

                    <div className="text-center mt-6">
                      <Link to="/registrarse" state={creditUser}>
                        <Primary text="Solicitar crédito" />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </section>
    </div>
  );
}
